/* Coupon removal never changes artwork, line properties, quantities or AI access. */
(function () {
  'use strict';
  const root = window.Shopify?.routes?.root || '/';
  const copy = JSON.parse(document.getElementById('rp-coupon-copy')?.textContent || '{}');
  const storageKey = 'robopixel:removed-coupons:v1';
  const attribute = '_rp_removed_coupons';
  const normalize = code => String(code || '').trim().toUpperCase();
  const text = (key, values = {}) => Object.entries(values).reduce((s, [k, v]) => s.replace('{' + k + '}', v), copy[key] || key);
  let busy = false;
  const memory = new Set();
  const parse = value => { try { return JSON.parse(value || '[]').map(normalize); } catch (_) { return []; } };
  async function cart() {
    const response = await fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw Error(text('error'));
    return response.json();
  }
  async function refreshCart() {
    if (window.RoboPixelCartSync) return window.RoboPixelCartSync.refresh();
    const targets = [{ section: 'cart-drawer', selector: '#CartDrawer' },
      { section: 'cart-icon-bubble', selector: '#cart-icon-bubble', source: '.shopify-section' }];
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const node = document.getElementById(id);
      if (node?.dataset.id) targets.push({ section: node.dataset.id, selector: '#' + id });
    }
    const url = new URL(root, location.origin);
    url.searchParams.set('sections', [...new Set(targets.map(t => t.section))].join(','));
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) return false;
      const sections = await response.json();
      const updates = targets.filter(t => document.querySelector(t.selector)).map(t => {
        if (typeof sections[t.section] !== 'string') throw Error('missing section');
        const source = new DOMParser().parseFromString(sections[t.section], 'text/html').querySelector(t.source || t.selector);
        if (!source) throw Error('missing section');
        return { target: document.querySelector(t.selector), source };
      });
      updates.forEach(({ target, source }) => { target.innerHTML = source.innerHTML; });
      const drawer = document.querySelector('cart-drawer');
      drawer?.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
      if (drawer?.classList.contains('active') && typeof trapFocus === 'function') {
        const panel = drawer.querySelector('.drawer__inner');
        trapFocus(panel, panel.querySelector('[data-coupon-buttons] button') || panel.querySelector('.drawer__close') || panel);
      } else document.querySelector('#main-cart-footer [data-coupon-buttons] button, #checkout')?.focus();
      return true;
    } catch (_) { return false; }
  }
  function localSuppressed() {
    try { return parse(localStorage.getItem(storageKey)); } catch (_) { return []; }
  }
  async function isSuppressed(code) {
    code = normalize(code);
    if (memory.has(code) || localSuppressed().includes(code)) return true;
    // Cart attributes persist the choice even when browser storage is blocked.
    try { return parse((await cart()).attributes?.[attribute]).includes(code); }
    catch (_) { return true; /* Never auto-apply a credit when removal cannot be checked. */ }
  }
  async function remove(code) {
    if (busy || window.__rpCustomizerCartBusy || window.RoboPixelPairJourney?.isAdding()) throw Error(text('busy'));
    busy = true;
    code = normalize(code);
    try {
      const before = await cart();
      const remaining = (before.discount_codes || []).filter(d => normalize(d.code) !== code).map(d => d.code);
      const suppressed = [...new Set([...parse(before.attributes?.[attribute]), ...localSuppressed(), code])];
      let writeError;
      try {
        const response = await fetch(root + 'cart/update.js', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: remaining.join(','), attributes: { [attribute]: JSON.stringify(suppressed) } })
        });
        if (!response.ok) throw Error(text('error'));
      } catch (error) { writeError = error; }
      // Reconcile a lost response; never report success on HTTP status alone.
      const after = await cart();
      if ((after.discount_codes || []).some(d => normalize(d.code) === code) ||
          !parse(after.attributes?.[attribute]).includes(code)) throw writeError || Error(text('error'));
      memory.add(code);
      try { localStorage.setItem(storageKey, JSON.stringify(suppressed)); } catch (_) {}
      busy = false;
      window.dispatchEvent(new Event('robopixel:cart-changed'));
      await window.RoboPixelPairJourney?.refresh();
      const painted = await refreshCart();
      document.querySelectorAll('cart-coupon-controls').forEach(node => node.render((after.discount_codes || []).map(d => d.code)));
      return painted === false ? text('refreshError') : text('removed');
    } finally { busy = false; }
  }
  function money(cents, currency = 'EUR') {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency, minimumFractionDigits: cents % 100 === 0 ? 0 : 2 }).format(cents / 100);
  }
  function confirmQuote(quote) {
    return new Promise(resolve => {
      const previous = document.activeElement;
      const openDrawer = document.querySelector('cart-drawer.active');
      if (openDrawer && typeof removeTrapFocus === 'function') removeTrapFocus();
      const dialog = document.createElement('dialog');
      dialog.className = 'rp-coupon-dialog';
      dialog.setAttribute('aria-labelledby', 'rp-coupon-quote-title');
      const title = document.createElement('h2'); title.id = 'rp-coupon-quote-title'; title.textContent = text('quoteTitle');
      const body = document.createElement('p'); body.textContent = text('quoteBody', { price: money(quote.increment), total: money(quote.total) });
      const actions = document.createElement('div'); actions.className = 'rp-coupon-dialog__actions';
      const restoreFocus = () => {
        if (openDrawer?.classList.contains('active') && typeof trapFocus === 'function') {
          const panel = openDrawer.querySelector('.drawer__inner');
          trapFocus(panel, previous?.isConnected && !previous.disabled ? previous : panel);
        } else previous?.focus?.();
      };
      const finish = result => { dialog.close(); dialog.remove(); restoreFocus(); resolve(result); };
      for (const [label, result, style] of [['keep', true, 'button--primary'], ['review', 'cart', 'button--secondary'], ['cancel', false, 'button--tertiary']]) {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'button ' + style;
        button.textContent = text(label); button.addEventListener('click', () => finish(result)); actions.append(button);
      }
      dialog.append(title, body, actions); document.body.append(dialog);
      dialog.addEventListener('cancel', event => { event.preventDefault(); finish(false); });
      if (!dialog.showModal) { dialog.remove(); restoreFocus(); resolve(false); return; }
      dialog.showModal();
    });
  }
  class CouponControls extends HTMLElement {
    connectedCallback() {
      this.render(JSON.parse(this.querySelector('[data-coupon-codes]')?.textContent || '[]'));
    }
    render(codes) {
      const container = this.querySelector('[data-coupon-buttons]');
      if (!container) return;
      container.replaceChildren();
      for (const code of [...new Set(codes.filter(Boolean))]) {
        const row = document.createElement('div'); row.className = 'rp-coupon-row caption';
        const name = document.createElement('span'); name.textContent = code;
        const button = document.createElement('button'); button.type = 'button'; button.textContent = text('remove');
        button.setAttribute('aria-label', text('removeLabel', { code }));
        button.addEventListener('click', async () => {
          button.disabled = true;
          const setStatus = message => document.querySelectorAll('[data-coupon-status]').forEach(node => { node.textContent = message; });
          setStatus(text('removing'));
          try { setStatus(await remove(code)); }
          catch (error) { setStatus(error.message || text('error')); }
          finally { button.disabled = false; }
        });
        row.append(name, button); container.append(row);
      }
    }
  }
  window.RoboPixelCoupons = { remove, isSuppressed, confirmQuote, refreshCart, text, isBusy: () => busy };
  customElements.define('cart-coupon-controls', CouponControls);
  // The shared journey refresh paints coupon-aware copy on existing offer widgets.
  window.RoboPixelPairJourney?.refresh().catch(() => {});
})();
